import React, {useMemo} from 'react';
import {Stack} from '@mui/material';
import {Page, StorePageCount} from '../../components/page/Page';
import {KeyVault} from '../../api/Model';
import {useEmployeeNeighbourhoodStore} from "../../api/EmployeeNeighbourhoodStore";
import {useEmployeeHierarchyStore} from "../../api/EmployeeHierarchyStore";
import {useFilter} from "../../util/FilterSerializer";
import {
    employeePortraitFilterFormSerializer,
    EmployeePortraitListFilter,
    filterEmployeePortraits
} from "./EmployeePortraitListFilter";
import {useEmployeeKeyHistoryStore} from "../../api/EmployeeKeyHistoryStore";
import {RemoteValue} from "../../util/RemoteValue";
import {LoadingData} from "../../components/loading-data/LoadingData";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import {ResidenceModel} from "../../api/ResidenceStore";


export default function EmployeePortraitListPage({defaultKeyVault, residenceModel, keyVaults}: {
    defaultKeyVault: KeyVault,
    keyVaults: KeyVault[],
    residenceModel: ResidenceModel,
}) {
    const employeeNeighbourhoods = useEmployeeNeighbourhoodStore(state => state.remoteValue);
    const unfiltered = useEmployeeHierarchyStore(employeeNeighbourhoods);
    const employeeKeyHistory = useEmployeeKeyHistoryStore(state => state.remoteValue);
    const filterSerializer = useMemo(
        () => employeePortraitFilterFormSerializer(defaultKeyVault, keyVaults, residenceModel),
        [defaultKeyVault, keyVaults, residenceModel]
    );
    const [filter, setFilter] = useFilter(filterSerializer);

    const filtered = useMemo(
        () => RemoteValue.combine({employees: unfiltered, employeeKeyHistory})
            .map(({employees: allEmployees, employeeKeyHistory}) => {
                    return filterEmployeePortraits(allEmployees.employees, employeeKeyHistory, filter);
                }
            ),
        [filter, unfiltered, employeeKeyHistory]
    );

    return (
        <Page
            header="Employee Portraits"
            counter={<StorePageCount totalValue={unfiltered} value={filtered}/>}
            filter={<EmployeePortraitListFilter filter={filter} setFilter={setFilter}/>}
        >
            <Stack gap={1} direction="row" flexWrap="wrap">
                <LoadingData
                    value={filtered}
                    totalValue={unfiltered}
                    defaultErrorMessage="Failed to load employees data. Please try again later."
                    emptyMessage="No employees found."
                    noMatchMessage="No employees match the current filters. Try adjusting your filter criteria."
                >{employees =>
                    employees.map(employee => (
                        <EmployeeAvatar employee={employee} key={employee.id} showNameTooltip/>
                    ))
                }</LoadingData>
            </Stack>
        </Page>
    );
}
